// @givmo/app-charity — the backend user-row BOOTSTRAP for cold visitors.
//
// A Firebase sign-in mints no Givmo `users` row: the backend middleware only LOADS by `idp_id`
// (no auto-provisioning outside local), and this app deliberately makes no company-registration
// call at sign-up. Consumers get their row from the mobile apps' register-on-403 dance, and the
// donate app provisions inline at p2p join — the charity portal did NEITHER, so a truly cold
// visitor (outreach's entire audience) signed in and then every `/charity-admin` read 403'd as
// "Couldn't load your organizations", and the email path 403'd at the OTP send. Masked for months
// because every tester already had a consumer/admin account (found 2026-08-18 via the first-ever
// Microsoft sign-in on prod).
//
// The cure is the donate pattern: `GET /users/me` → a 403 means "no row" (the backend deliberately
// answers 403, not 404) → `POST /users` (identity comes from the ID token; the email rides along
// when Firebase has one) → tolerate a 409 create-race only if the identity then resolves.
//
// Called from the auth-state chokepoint (auth-context publishes the session only after this
// settles, so no downstream query can race the row into a 403), from `sendOtp` (the sign-up flow
// fires it before the listener may have finished), and from the memberships "Try again" through
// auth-context's `retryBackendUserBootstrap` (so a bootstrap that failed on a flaky network heals on
// retry, not on reload — and re-establishes the suite session once it does, within 300 s of the
// sign-in; a later retry leaves that to the next real sign-in, AS-SUITE11). One in-flight promise
// per uid; a FAILED attempt clears its slot so the next call actually retries — a memoized rejection
// would otherwise brick the session until reload.
//
// Every request it sends is bounded (CPA-WEB89): the session publishes only once this settles, and
// the console's guards wait on that publish (CPA-WEB86), so a request that never answered held the
// sign-in screen up for as long as it stayed open. Past the bound it fails like any other failure:
// the session publishes, and the memberships card says so with its "Try again".

import { sdk, isGivmoError, mapTransportError } from '@givmo/api-client';

/**
 * How long one bootstrap request may go unanswered before it counts as failed (CPA-WEB89) — its App
 * Check mint and ID token included, which run in front of the fetch and which nothing else bounds.
 *
 * Sized from 30 days of Cloud Run request logs (2026-09-21 to 2026-10-20, server-side time):
 * `GET /users/me` answered within 1.2 s on prod and 3.3 s on dev, but `POST /users`, the create in
 * front of a new account's first sign-in, ran past 3 s for 0.1–0.2% of creates on dev and staging.
 * With the network on top, 4 s cut those new accounts off. 6 s leaves about 2 s above every request in
 * those logs but a handful of 8–22 s outliers, which no wait short of a hung app would rescue. No cold
 * Cloud Run instance sat in front of `/users/me` in those 30 days, and none could be waited out here:
 * one takes about a minute to boot.
 *
 * Not the suite handoff's 4 s (`@givmo/auth` `DEFAULT_ADOPT_TIMEOUT_MS`): that one holds a splash
 * before first paint, this one a sign-in the person has just pressed. A cutoff costs an account whose
 * row exists nothing, because the session publishes anyway; a new account gets the memberships card's
 * "Try again", or on sign-up the verify screen's resend, and both recover.
 */
export const BOOTSTRAP_REQUEST_TIMEOUT_MS = 6_000;

const attempts = new Map<string, Promise<void>>();

function hasStatus(error: unknown, status: number): boolean {
  return isGivmoError(error) && error.status === status;
}

/**
 * One bootstrap request, given up at the bound: aborted, and failed the way a request with no answer
 * fails, whatever is still pending in front of its fetch.
 */
async function bounded<T>(send: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expired = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(mapTransportError({}));
    }, BOOTSTRAP_REQUEST_TIMEOUT_MS);
  });
  try {
    return await Promise.race([send(controller.signal), expired]);
  } finally {
    clearTimeout(timer);
  }
}

async function provision(email: string | null): Promise<void> {
  const existing = await bounded((signal) => sdk.usersGetMe({ signal }));
  if (existing.data !== undefined) return;
  if (!hasStatus(existing.error, 403)) {
    throw existing.error ?? new Error('Your Givmo account could not be confirmed.');
  }

  const created = await bounded((signal) =>
    sdk.usersCreateUser({ body: email ? { email } : {}, signal }),
  );
  if (created.data !== undefined) return;

  if (hasStatus(created.error, 409)) {
    // A second auth callback can race this one. Only tolerate the conflict if the authenticated
    // identity now resolves; otherwise it may be a different Firebase UID owning the same email.
    const confirmed = await bounded((signal) => sdk.usersGetMe({ signal }));
    if (confirmed.data !== undefined) return;
  }

  throw created.error ?? new Error('Your Givmo account could not be created.');
}

/**
 * Ensure the signed-in Firebase user has a backend `users` row. Idempotent and deduplicated per
 * uid: concurrent callers share one in-flight attempt, a settled success stays cached for the
 * session, and a failure clears the slot so the next caller retries for real.
 */
export function ensureBackendUser(uid: string, email: string | null): Promise<void> {
  const existing = attempts.get(uid);
  if (existing !== undefined) return existing;
  const attempt = provision(email).catch((thrown: unknown) => {
    attempts.delete(uid);
    throw thrown;
  });
  attempts.set(uid, attempt);
  return attempt;
}

/** Test seam: forget every cached attempt (suites share module state). */
export function resetEnsureBackendUser(): void {
  attempts.clear();
}
